import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dropdown,
  Flex,
  Layout,
  type MenuProps,
  Typography,
} from 'antd';
import { Auth } from 'aws-amplify';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import IconArrowRight from '@/assets/icons/arrow-right.svg?react';
import IconLogo from '@/assets/icons/logo.svg?react';
import IconUpdate from '@/assets/icons/update.svg?react';
import IconUser from '@/assets/icons/user.svg?react';
import { ROUTES_PATH } from '@/constants/routes.constants';
import { useModalMethod } from '@/hooks/use-modal-method';
import { useCommonStore } from '@/store/common.store';

const { Text, Title } = Typography;

interface HeaderProps {
  includeSetting?: boolean;
}

export function Header({ includeSetting = true }: Readonly<HeaderProps>) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { confirm } = useModalMethod();
  const { myAccountName, setIsFormChanged } = useCommonStore(
    useShallow(({ myAccountName, setIsFormChanged }) => ({
      myAccountName,
      setIsFormChanged,
    })),
  );

  function handleLogout() {
    confirm({
      okText: 'ログアウト',
      title: 'ログアウトしますか？',
      onOk: async () => {
        setIsFormChanged(false);
        queryClient.clear();
        await Auth.signOut();
        navigate(ROUTES_PATH.login);
      },
    });
  }

  function handleRedirectChangePassword() {
    navigate(ROUTES_PATH.changePassword, { state: { from: pathname } });
  }

  function handleRedirectMyAccount() {
    navigate(ROUTES_PATH.myAccount, { state: { from: pathname } });
  }

  const dropdownItems: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <Flex align="center" justify="space-between">
          <Text className="flex-1 text-ellipsis">{myAccountName}</Text>
          <IconUpdate className="ml-8" />
        </Flex>
      ),
      onClick: handleRedirectMyAccount,
    },
    { type: 'divider' },
    {
      key: '2',
      label: (
        <Flex align="center" justify="space-between">
          <Text>パスワード変更</Text>
          <IconArrowRight className="ml-8" />
        </Flex>
      ),
      onClick: handleRedirectChangePassword,
    },
    {
      key: '3',
      label: (
        <Flex align="center" justify="space-between">
          <Text>ログアウト</Text>
          <IconArrowRight className="ml-8" />
        </Flex>
      ),
      onClick: handleLogout,
    },
  ];

  return (
    <Layout.Header>
      <Title level={1}>
        <Flex align="center">
          <IconLogo />
          <Text className="header-title ml-16">サービス管理画面</Text>
        </Flex>
      </Title>
      {includeSetting && (
        <Dropdown
          destroyPopupOnHide
          menu={{ items: dropdownItems }}
          trigger={['click']}
          className="cursor-pointer"
          overlayClassName="ant-dropdown-setting"
        >
          <Button
            type="primary"
            className="ant-btn-setting"
            icon={<IconUser />}
          >
            <Text>{myAccountName}</Text>
          </Button>
        </Dropdown>
      )}
    </Layout.Header>
  );
}
